import { useState } from "react";
import { toast } from "sonner";
import { FileDown, RefreshCw, RotateCcw, Loader2 } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { downloadBlob } from "./SwotDialog";

const SYSTEMS = [["s1", "Systeem 1", "Uitvoering"], ["s2", "Systeem 2", "Coördinatie"], ["s3", "Systeem 3", "Sturing & optimalisatie"], ["s4", "Systeem 4", "Toekomst & omgeving"], ["s5", "Systeem 5", "Identiteit & beleid"]];
const QUADS = [["sterktes", "Sterktes"], ["zwaktes", "Zwaktes"], ["kansen", "Kansen"], ["bedreigingen", "Bedreigingen"]];
const inline = (t) => t.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : p));
const Markdown = ({ text }) => (
  <div className="text-sm leading-relaxed space-y-1" data-testid="swot-conclusion">
    {text.split("\n").map((l, i) => (/^#{1,6}\s/.test(l)
      ? <h4 key={i} className="font-grotesk font-semibold text-base pt-3">{inline(l.replace(/^#{1,6}\s*/, ""))}</h4>
      : l.trim() ? <p key={i}>{inline(l)}</p> : null))}
  </div>
);

const ScoreBars = ({ scores }) => (
  <div className="space-y-2" data-testid="swot-scores">
    {SYSTEMS.map(([k, n, d]) => {
      const pct = Math.round((scores[k] || 0) * 100);
      return (
        <div key={k} className="grid grid-cols-[140px_1fr_48px] items-center gap-3 text-sm">
          <div><div className="font-medium">{n}</div><div className="text-[11px] text-zinc-500">{d}</div></div>
          <div className="h-2 bg-zinc-200"><div className="h-2 bg-zinc-900" style={{ width: `${pct}%` }} /></div>
          <div className="text-right font-mono font-semibold" data-testid={`swot-score-${k}`}>{pct}%</div>
        </div>
      );
    })}
  </div>
);

const SwotGrid = ({ swot }) => (
  <div className="space-y-4" data-testid="swot-grid">
    {SYSTEMS.filter(([k]) => swot[k]).map(([k, n, d]) => (
      <div key={k} data-testid={`swot-system-${k}`}>
        <div className="font-grotesk font-semibold text-sm mb-1">{n} · {d}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 border-l border-t border-zinc-900">
          {QUADS.map(([q, t]) => (
            <div key={q} className="border-r border-b border-zinc-900 p-3 text-sm">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{t}</div>
              <ul className="list-disc pl-4 space-y-0.5">{(swot[k][q] || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export const SwotOverview = ({ data, orgId, canEdit, reload }) => {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const base = `/organizations/${orgId}/swot`;

  const refresh = async () => {
    setBusy(true);
    try { await api.post(`${base}/aggregate`); await reload(); } catch { toast.error("Bijwerken mislukt"); }
    setBusy(false);
  };
  const reset = async () => {
    try { await api.post(`${base}/reset`); toast.success("AI SWOT is gereset; een nieuwe ronde is gestart"); await reload(); } catch { toast.error("Resetten mislukt"); }
  };

  return (
    <div className="space-y-6 pt-2" data-testid="swot-overview">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm text-zinc-600 mr-auto" data-testid="swot-participants">
          Ronde {data.round} · {data.completed} van {data.participants} gesprek(ken) afgerond
        </div>
        {data.completed > 0 && (
          <Button variant="outline" className="rounded-none h-9" onClick={() => downloadBlob(`${base}/rounds/${data.round}/report.pdf`, `AI-SWOT-ronde-${data.round}.pdf`)} data-testid="swot-report-download">
            <FileDown className="h-4 w-4 mr-2" /> Rapport (PDF)
          </Button>
        )}
        {canEdit && data.completed > 0 && (
          <Button variant="outline" className="rounded-none h-9" onClick={refresh} disabled={busy} data-testid="swot-refresh-button">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />} Conclusie bijwerken
          </Button>
        )}
        {data.can_reset && (
          <Button variant="outline" className="rounded-none h-9" onClick={() => setConfirm(true)} data-testid="swot-reset-button">
            <RotateCcw className="h-4 w-4 mr-2" /> Reset
          </Button>
        )}
      </div>
      {data.completed === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="swot-empty">Nog niemand heeft de AI SWOT afgerond in deze ronde.</p>
      ) : (
        <>
          <ScoreBars scores={data.scores} />
          <div>
            <h3 className="font-grotesk font-semibold mb-2">Conclusie van Robin</h3>
            {data.conclusion ? (
              <Markdown text={data.conclusion} />
            ) : (
              <p className="text-sm text-zinc-500" data-testid="swot-conclusion-pending">Robin stelt de conclusie op basis van alle gesprekken op. Dit duurt ongeveer een minuut.</p>
            )}
            {data.generated_at && data.aggregated_participants !== data.completed && (
              <p className="text-xs text-zinc-500 mt-2">Deze conclusie is gebaseerd op {data.aggregated_participants} gesprek(ken); een nieuwere versie volgt.</p>
            )}
          </div>
          {data.swot && <SwotGrid swot={data.swot} />}
        </>
      )}
      {data.history?.length > 0 && (
        <div data-testid="swot-history">
          <h3 className="font-grotesk font-semibold mb-2">Eerdere rondes</h3>
          {data.history.map((h) => (
            <div key={h.round} className="flex items-center justify-between border-b border-zinc-200 py-2 text-sm">
              <span>Ronde {h.round} · {h.completed} gesprek(ken){h.reset_at ? ` · gereset op ${new Date(h.reset_at).toLocaleDateString("nl-NL")}` : ""}</span>
              <Button variant="ghost" className="rounded-none h-8" onClick={() => downloadBlob(`${base}/rounds/${h.round}/report.pdf`, `AI-SWOT-ronde-${h.round}.pdf`)} data-testid={`swot-history-download-${h.round}`}>
                <FileDown className="h-4 w-4 mr-1" /> PDF
              </Button>
            </div>
          ))}
        </div>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>AI SWOT resetten?</AlertDialogTitle>
            <AlertDialogDescription>Er start een nieuwe ronde: iedereen kan de AI SWOT opnieuw doen. De huidige uitslag blijft bewaard onder Eerdere rondes.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="swot-reset-cancel">Annuleren</AlertDialogCancel>
            <AlertDialogAction className="rounded-none" onClick={reset} data-testid="swot-reset-confirm">Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
